import moment, { type Moment } from "moment";
import type { IUsageWalletConstructorParams } from "../interfaces";

export enum UsageWalletTypeEnum {
	CALLS = "CALLS",
	SMS = "SMS",
	DATA = "DATA",
	NONE = "NONE",
}

const UsageWalletTypeReverseMap: { [key: string]: UsageWalletTypeEnum } = {
	CALLS: UsageWalletTypeEnum.CALLS,
	SMS: UsageWalletTypeEnum.SMS,
	DATA: UsageWalletTypeEnum.DATA,
};

const identity = <T>(val: T): T => val;

const UsageWalletTypeMap = new Map<UsageWalletTypeEnum, Object>([
	[
		UsageWalletTypeEnum.CALLS,
		{
			multiplier: 60,
			roundingFn: (val: number) => {
				return val % 1 === 0 ? val : val.toFixed(1);
			},
			unit: "",
			iconRef: "phone",
			label: "Mins left",
			order: 3,
		},
	],
	[
		UsageWalletTypeEnum.SMS,
		{
			multiplier: 1,
			roundingFn: identity,
			unit: "",
			iconRef: "comment",
			label: "SMS left",
			order: 2,
		},
	],
	[
		UsageWalletTypeEnum.DATA,
		{
			multiplier: 1024 * 1024,
			roundingFn: (val: number) => val.toFixed(2),
			unit: "GB",
			iconRef: "cloud",
			label: "Data left",
			order: 1,
		},
	],
	[
		UsageWalletTypeEnum.NONE,
		{
			multiplier: 1,
			roundingFn: identity,
			unit: "",
			iconRef: "sphere",
			label: "Units left",
			order: 0,
		},
	],
]);

const getFromUnitTypeMap = (key: UsageWalletTypeEnum): any => {
	const res = UsageWalletTypeMap.get(key);

	if (res === undefined) {
		return UsageWalletTypeMap.get(UsageWalletTypeEnum.NONE);
	}

	return res;
};

class UsageWallet {
	responseData: IUsageWalletConstructorParams;

	public static Construct(params: IUsageWalletConstructorParams): UsageWallet {
		return new UsageWallet(params);
	}

	constructor(params: IUsageWalletConstructorParams) {
		this.responseData = params;
	}

	private get typeMap(): {
		multiplier: number;
		roundingFn: Function;
		unit: string;
		iconRef: string;
		label: string;
		order: number;
	} {
		return getFromUnitTypeMap(this.walletType);
	}

	private applyRoundedMultiplier = (val: any): any => {
		const { roundingFn, multiplier } = this.typeMap;

		return roundingFn(val / multiplier);
	};

	get order(): number {
		return this.typeMap.order;
	}

	get progress(): number {
		const total = this.responseData.allowanceTotal;
		const remaining = this.responseData.allowanceRemaining;
		return ((total - remaining) / total) * 100;
	}

	get data(): string | number {
		return this.applyRoundedMultiplier(this.responseData.allowanceRemaining);
	}

	get total(): string | number {
		return this.applyRoundedMultiplier(this.responseData.allowanceTotal);
	}

	get walletType(): UsageWalletTypeEnum {
		const res = UsageWalletTypeReverseMap[this.responseData.walletType];

		if (!res) {
			return UsageWalletTypeEnum.NONE;
		}

		return res;
	}

	get unit(): string {
		return this.typeMap.unit;
	}

	get iconRef(): string {
		return this.typeMap.iconRef;
	}

	get label(): string {
		return this.typeMap.label;
	}

	get expiry(): Moment {
		return moment(this.responseData.expiry);
	}
}

export default UsageWallet;
